// Navigator.js
// =============================================================================
// A floating "where am I, and how do I…" panel.
// =============================================================================
//
// Opened by "Show navigator" at the foot of the left rail. Movable by its
// header, closable by the X, and always on top. It stays open while you
// navigate — that is the point: you can read the answer and follow it at the
// same time, which a modal dialog makes impossible.
//
// Three sections:
//   1. WHERE YOU ARE — driven by currentView. Updates as you move.
//   2. ASK — a question box.
//   3. ANSWER — the path to the thing, the steps, and a button that takes you
//      there where the destination is a top-level route.
//
// WHY THIS IS A KNOWLEDGE BASE AND NOT AN LLM CALL
// ------------------------------------------------
// A navigator's whole value is being RIGHT about where things are. An answer
// that invents a plausible-sounding menu path ("Settings → Staff → Schedules")
// is worse than no answer, because the user goes looking and concludes the app
// is broken rather than the help. Every path below is taken from the source, so
// it is either correct or a bug someone can fix.
//
// The cost is coverage: it only knows what is in HOW_TO. So when nothing
// matches, it says so plainly and offers the topics it does hold, rather than
// generating something confident and wrong. If this is later backed by a model,
// keep that division — let the model REPHRASE these entries, not invent paths.
//
// KEEPING IT TRUE
// ---------------
// These entries describe the UI. When the UI moves, they go stale silently —
// nothing will fail a build. The `where` line of any entry you touch during a
// change is worth updating in the same commit.
// =============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";

/* ========================================================================== */
/* 1. WHERE YOU ARE — one entry per top-level route                           */
/* ========================================================================== */
const CONTEXT = {
  head_office: {
    title: "Delivery control",
    blurb: "The live fleet dashboard — every branch's customers, drivers and vehicles, today's rounds and deliveries, the failed-attempts queue, and a live map, all at a glance.",
    can: [
      "Open the Customers card to look someone up, or add a new customer",
      "Check the driver roster's shift and mileage figures, and vehicle MOT/service dates coming due",
      "Click an open round to see its remaining stops and their ETA",
      "Search today's deliveries by name, address, postcode, reference or bag barcode",
      "Work through the exception queue — parcels that have failed at least once and aren't finished",
      "Watch every driver currently out on the live map, or move it to its own browser tab"
    ],
    related: ["customer-lookup", "round-detail", "failed-attempts-queue", "fleet-map-fullscreen"]
  },
  customers: {
    title: "Customers",
    blurb: "One register for the whole group. A customer belongs to the group, not a branch — their deliveries and collections belong to whichever branch sent or is collecting them. Reached from the Customers card on Delivery control, not from the left rail.",
    can: [
      "Search by name, address, postcode, phone, reference or bag barcode",
      "Open a customer to see every delivery and collection against them, across every branch",
      "Add a delivery or a collection, and — for a collection — assign whichever driver is closest"
    ],
    related: ["customer-lookup", "create-delivery"]
  },
  analytics: {
    title: "Delivery analytics",
    blurb: "Three tabs, each a different slice of delivery performance: Analytics (today's activity by branch, a date-range summary, deliveries over time, and two per-driver charts), Summary (deliveries, mileage and time over a date range, by site and driver — a different \"Summary\" from the KPI card inside the Analytics tab), and Delivery rounds (per-round route efficiency).",
    can: [
      "See outstanding, completed, failed and collections for today, branch by branch",
      "Watch completed and failed deliveries over up to six months, for one branch or all of them",
      "Compare deliveries per hour and miles-vs-hours by driver, by month",
      "Compare drivers and sites over a chosen date range (Summary tab)",
      "Look at a round and see where the time and mileage went (Delivery rounds tab)"
    ],
    related: ["analytics-tabs"]
  },
  communications: {
    title: "Communications",
    blurb: "Three tabs: Send push to driver app, Email staff/customers, and Sent email log.",
    can: [
      "Push a message straight to one or more drivers' phones, and see whether it actually arrived",
      "See the whole message thread with one driver",
      "Email a chosen set of drivers, or a searched set of customers, with a subject and message",
      "Look back through everything sent by email"
    ],
    related: ["message-drivers", "email-people", "sent-log"]
  },
  dashpanels: {
    title: "Live monitoring panels",
    blurb: "Monitoring panels built from a plain-English description, refreshed on an interval you choose.",
    can: [
      "Describe a panel in ordinary words — deliveries, collections, rounds or attempts — and have it built",
      "Arrange panels and save the arrangement as a named dashboard"
    ],
    related: ["dashboards"]
  },
  organisation: {
    title: "Configuration",
    blurb: "Four tabs: Organisation, Branches, People, and Locations and messaging. All four read and write the same underlying group record, so a new branch shows up immediately in People's branch picker.",
    can: [
      "Add or edit a branch",
      "Add a person, set their role (driver, employee, branch manager or group manager) and which branch(es) they belong to, or deactivate someone who has left",
      "Manage the shelves and bays inside a branch, driver totes, and the messages customers get when their parcel arrives (Locations and messaging tab)"
    ],
    related: ["add-branch", "add-person", "locations-messaging"]
  },
  config: {
    title: "Configurables",
    blurb: "Admin-editable values used across the platform. Only visible on specific admin accounts, under an \"Admin\" heading in the rail.",
    can: ["Change a value once and have everything that uses it follow"],
    related: []
  },
  // route_analysis and delivery_activity are no longer reachable as their own
  // currentView — both are tabs inside "analytics" above (Delivery rounds and
  // Summary respectively). Left here, harmlessly orphaned, in case something
  // still deep links to them.
  route_analysis: {
    title: "Delivery analytics → Delivery rounds",
    blurb: "Delivery driver routes and how efficiently they run.",
    can: ["Look at a round and see where the time and mileage go"],
    related: []
  },
  delivery_activity: {
    title: "Delivery analytics → Summary",
    blurb: "Deliveries, mileage and time over a date range, by site and driver.",
    can: ["Compare drivers and sites over a period"],
    related: []
  }
};

const FALLBACK_CONTEXT = {
  title: "Delivery",
  blurb: "Pick a section from the menu on the left. Delivery control is the usual starting point — the live status of the whole fleet, right now.",
  can: [],
  related: ["customer-lookup", "round-detail", "message-drivers", "analytics-tabs"]
};

/* ========================================================================== */
/* 2. HOW DO I… — the answerable questions                                    */
/* ========================================================================== */
// `where` is the click path, as it actually is in the app.
// `view` is a top-level route, so the panel can offer to take you there. Leave
//        it out for anything reached through a dialog or a tab — offering to
//        navigate somewhere the button cannot actually reach is worse than not
//        offering. `view` can point at a route with no rail button of its own
//        (e.g. "customers") — goTo() sets the screen directly, so the button
//        still works even though there's nothing to click in the left rail.
const HOW_TO = [
  {
    id: "customer-lookup",
    title: "Look up a customer and see their delivery history",
    keywords: ["customer", "customers", "lookup", "look up", "search customer", "find customer",
               "delivery history", "address", "postcode", "phone number", "where is my parcel"],
    where: "Delivery control → Customers card",
    view: "customers",
    steps: [
      "Open Delivery control (the default screen) and click the green Customers card, top-left.",
      "Search by name, address, postcode, phone, reference or bag barcode.",
      "Open a customer to see every delivery and collection against them, across every branch that has sent or collected from them."
    ],
    note: "A customer is one record for the whole group — their deliveries belong to whichever branch sent them, not to the customer."
  },
  {
    id: "add-customer",
    title: "Add a new customer",
    keywords: ["add customer", "new customer", "create customer", "register customer"],
    where: "Delivery control → Customers card → New",
    view: "customers",
    steps: [
      "Open the Customers card from Delivery control.",
      "Press \"New\", above the search box.",
      "Fill in their details and save."
    ]
  },
  {
    id: "create-delivery",
    title: "Raise a delivery or a collection",
    keywords: ["create delivery", "new delivery", "raise delivery", "book delivery", "add parcel",
               "collection", "collect", "raise collection", "book collection", "assign driver",
               "pickup", "pick up"],
    where: "Delivery control → Customers → open a customer → Add a delivery",
    view: "customers",
    steps: [
      "Open the customer (see \"Look up a customer\").",
      "Press \"Add a delivery\".",
      "The form defaults to a delivery — switch it to a collection if that's what this is.",
      "For a collection, pick the branch first: that decides the driver roster you're offered, ranked by distance from the pickup postcode.",
      "Save. A delivery needs a branch too — it's the only place a branch enters this screen, since the customer itself isn't tied to one."
    ]
  },
  {
    id: "round-detail",
    title: "See what's left on a driver's round, and its ETA",
    keywords: ["round", "rounds", "open rounds", "stops", "eta", "estimated arrival",
               "remaining drops", "driver progress", "where is the driver"],
    where: "Delivery control → Open rounds",
    view: "head_office",
    steps: [
      "Open Delivery control and find the round in the Open rounds table, under \"On the road\".",
      "Click the row to expand it in place.",
      "Outstanding stops show their address and an ETA, pulled from the same live feed as the fleet map.",
      "Delivered and failed counts for that round are summarised above the stop list rather than itemised — this feed only carries what's still outstanding."
    ],
    note: "If a round shows nothing on expanding but still has stops outstanding, the driver likely hasn't reported a live position yet."
  },
  {
    id: "failed-attempts-queue",
    title: "Work through failed delivery attempts",
    keywords: ["failed", "failed attempts", "exception", "exceptions", "queue", "not delivered",
               "returned", "unresolved", "three attempts", "48 hours"],
    where: "Delivery control → Failed attempts",
    view: "head_office",
    steps: [
      "Open Delivery control and find Failed attempts, under \"On the road\".",
      "This is the exception queue: anything that has failed at least once and isn't finished, however long ago.",
      "Two flags to look for: 3+ attempts (time to ring the customer rather than send the van again) and failed 48 hours or more ago with nobody owning it."
    ]
  },
  {
    id: "fleet-map-fullscreen",
    title: "Put the fleet map on its own screen",
    keywords: ["fleet map", "map", "full screen", "fullscreen", "second monitor", "new tab",
               "move to new screen"],
    where: "Delivery control → Fleet map → Move to new screen",
    view: "head_office",
    steps: [
      "Open Delivery control. The fleet map sits in the right-hand column.",
      "Press \"Move to new screen\".",
      "The map opens full screen in a new browser tab, already signed in — drag that tab to another monitor and leave it running."
    ]
  },
  {
    id: "message-drivers",
    title: "Send a push notification to a driver",
    keywords: ["push", "push notification", "message driver", "message drivers", "notify driver",
               "text driver", "app message", "send message"],
    where: "Communications → Send push to driver app",
    view: "communications",
    steps: [
      "Open Communications — it opens on this tab.",
      "The banner at the top says whether push is actually live right now, and how many drivers have the app registered.",
      "Tick the drivers to message, write the message, and send.",
      "Each driver's result is honest about what happened — pushed, no phone registered, or push failed — never just \"sent\"."
    ],
    note: "A driver with no phone registered still gets the message; they'll see it next time they open the app rather than being pushed to."
  },
  {
    id: "email-people",
    title: "Email a group of drivers or customers",
    keywords: ["email drivers", "email customers", "email staff", "bulk email", "send email",
               "email group", "announce", "notify"],
    where: "Communications → Email staff/customers",
    view: "communications",
    steps: [
      "Open Communications and go to the Email staff/customers tab.",
      "Choose Drivers or Customers at the top of the list — customers can be searched by name, email or postcode.",
      "Tick who it goes to, write a subject and message, and send.",
      "Emails go out one at a time — recipients never see each other's address."
    ]
  },
  {
    id: "sent-log",
    title: "Check what's already been emailed",
    keywords: ["sent log", "email log", "history", "what did we send", "did they get it",
               "sent email", "email history"],
    where: "Communications → Sent email log",
    view: "communications",
    steps: [
      "Open Communications and go to the Sent email log tab.",
      "Everything sent by email is listed here, most recent first."
    ]
  },
  {
    id: "analytics-tabs",
    title: "Find the right view on Delivery analytics",
    keywords: ["analytics", "delivery analytics", "reports", "reporting", "charts", "graphs",
               "deliveries per day", "average delivery time", "driver hours", "miles per driver"],
    where: "Delivery analytics",
    view: "analytics",
    steps: [
      "Open Delivery analytics from the left rail.",
      "Analytics tab: today's activity by branch, a Summary card for a date range you pick, deliveries over time (up to six months, one branch or all), and two per-driver charts — deliveries per hour and miles vs. hours, both by month.",
      "Summary tab: deliveries, mileage and time over a date range, broken down by site and by driver.",
      "Delivery rounds tab: per-round route efficiency — where the time and mileage on a round actually went."
    ],
    note: "\"Hours\" on the two driver charts is time spent driving between drops, not clocked shift hours — worth knowing before comparing it to a timesheet."
  },
  {
    id: "dashboards",
    title: "Build a live monitoring panel",
    keywords: ["dashboard", "dashboards", "panel", "panels", "monitor", "monitoring", "live",
               "custom", "build panel"],
    where: "Live monitoring panels",
    view: "dashpanels",
    steps: [
      "Open Live monitoring panels from the left rail.",
      "Describe the panel you want in ordinary words — about deliveries, collections, rounds or attempts.",
      "Set the refresh interval, arrange the panels, and save the arrangement as a named dashboard."
    ]
  },
  {
    id: "add-branch",
    title: "Add or edit a branch",
    keywords: ["branch", "branches", "add branch", "new branch", "depot", "site", "edit branch"],
    where: "Configuration → Branches",
    view: "organisation",
    steps: [
      "Open Configuration from the left rail and go to the Branches tab.",
      "Add a branch, or open an existing one to edit it.",
      "A new branch shows up immediately in the People tab's branch picker — it's all one payload underneath."
    ]
  },
  {
    id: "add-person",
    title: "Add a person, set their role, or deactivate someone who's left",
    keywords: ["add person", "add staff", "new staff", "add driver", "role", "branch manager",
               "group manager", "employee", "deactivate", "remove staff", "who works where"],
    where: "Configuration → People",
    view: "organisation",
    steps: [
      "Open Configuration and go to the People tab.",
      "Add a person and pick their role: driver, employee, branch manager or group manager.",
      "Choose which branch or branches they belong to.",
      "To remove someone, deactivate rather than delete — their past deliveries, rounds and clockings stay attached to their record."
    ],
    note: "Only branch manager and group manager open anything on this website. A driver only reaches the driver app. Employee currently records who works where and grants no access at all — that is deliberate, not a bug."
  },
  {
    id: "locations-messaging",
    title: "Set up shelves, driver totes, or the message a customer gets on arrival",
    keywords: ["locations", "shelves", "bays", "totes", "messaging", "customer message",
               "arrival message", "notification to customer", "parcel arrived"],
    where: "Configuration → Locations and messaging",
    view: "organisation",
    steps: [
      "Open Configuration and go to the Locations and messaging tab.",
      "Set up the shelves and bays inside a branch, and the totes belonging to each driver.",
      "Configure the message a customer receives when their parcel reaches one of these locations."
    ]
  },
  {
    id: "configurables",
    title: "Change a platform-wide configurable value",
    keywords: ["configurable", "configurables", "admin value", "platform setting", "change setting"],
    where: "Configurables (Admin)",
    view: "config",
    steps: [
      "Configurables sits under an \"Admin\" heading in the rail — only visible on specific admin accounts.",
      "Change the value once; everything on the platform that reads it follows."
    ],
    note: "If you can't see this in the rail at all, it isn't available on your account."
  }
];


/* ========================================================================== */
/* 3. Matching                                                                */
/* ========================================================================== */
// Deliberately simple and inspectable: score by how many of the question's
// meaningful words appear in an entry's keywords or title. No cleverness that
// would make a wrong answer hard to explain.
const STOP = new Set([
  "how", "do", "i", "a", "an", "the", "to", "for", "of", "in", "on", "at", "is",
  "can", "you", "me", "my", "we", "our", "what", "where", "when", "which", "and",
  "or", "with", "it", "this", "that", "there", "get", "find", "please", "help",
  "want", "need", "does", "did", "am", "are", "be", "up", "out", "new", "set"
]);

const words = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);

function search(query) {
  const qs = words(query).filter(w => !STOP.has(w) && w.length > 1);
  if (!qs.length) return [];

  const scored = HOW_TO.map(entry => {
    const hay = [entry.title, ...(entry.keywords || []), entry.where].join(" ").toLowerCase();
    let score = 0;
    for (const w of qs) {
      // A keyword phrase matching exactly is worth more than an incidental
      // word appearing somewhere in the blob.
      if ((entry.keywords || []).some(k => k === w)) score += 3;
      else if ((entry.keywords || []).some(k => k.includes(w))) score += 2;
      else if (hay.includes(w)) score += 1;
    }
    // Title words carry extra weight — "schedule" in a title means more than
    // "schedule" mentioned in passing.
    for (const w of qs) if (words(entry.title).includes(w)) score += 2;
    return { entry, score };
  }).filter(r => r.score > 0).sort((a, b) => b.score - a.score);

  // Require a real signal, not one incidental word.
  return scored.filter(r => r.score >= 2).slice(0, 3).map(r => r.entry);
}

/* ========================================================================== */
/* Styles                                                                     */
/* ========================================================================== */
const P = {
  panel: (x, y, w, h) => ({
    position: "fixed", left: x, top: y, width: w, height: h,
    background: "#fff", border: "1px solid #cbd5e1", borderRadius: 10,
    boxShadow: "0 12px 36px rgba(15,23,42,0.25)",
    // Above the Settings and Shelves dialogs (z-index 1000) and the send
    // dialogue (9000) — "always on top" has to mean on top of those too, or it
    // disappears exactly when someone opens the thing they need help with.
    zIndex: 12000,
    display: "flex", flexDirection: "column", overflow: "hidden",
    fontFamily: "inherit"
  }),
  head: { display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "9px 12px", background: "#0f766e", color: "#fff",
          cursor: "move", userSelect: "none", flexShrink: 0 },
  body: { flex: 1, overflowY: "auto", padding: "12px 14px" },
  foot: { borderTop: "1px solid #e5e7eb", padding: "10px 12px", background: "#f8fafc", flexShrink: 0 },
  label: { fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase",
           letterSpacing: 0.5, marginBottom: 5 },
  card: { border: "1px solid #e5e7eb", borderRadius: 7, padding: "10px 12px", marginBottom: 10 },
  input: { width: "100%", boxSizing: "border-box", fontSize: 13, padding: "7px 9px",
           border: "1px solid #d1d5db", borderRadius: 5, fontFamily: "inherit" },
  go: { border: "1px solid #0f766e", background: "#f0fdfa", color: "#0f766e", borderRadius: 5,
        padding: "4px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer",
        fontFamily: "inherit", marginTop: 8 },
  link: { border: "none", background: "none", color: "#0f766e", fontSize: 12.5,
          fontWeight: 600, cursor: "pointer", padding: "3px 0", textAlign: "left",
          fontFamily: "inherit", display: "block" },
  where: { fontSize: 12, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4",
           borderRadius: 5, padding: "5px 8px", fontWeight: 600, marginBottom: 8 },
  step: { fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginBottom: 5,
          display: "flex", gap: 7 },
  note: { fontSize: 11.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a",
          borderRadius: 5, padding: "6px 8px", marginTop: 8, lineHeight: 1.45 },
  grip: { position: "absolute", right: 2, bottom: 2, width: 14, height: 14,
          cursor: "nwse-resize",
          background: "linear-gradient(135deg, transparent 50%, #cbd5e1 50%, #cbd5e1 60%, transparent 60%, transparent 72%, #cbd5e1 72%, #cbd5e1 82%, transparent 82%)" }
};

/* ========================================================================== */
/* The panel                                                                  */
/* ========================================================================== */
export default function Navigator({ currentView, onClose, goTo }) {
  const [pos, setPos] = useState(() => ({
    // Clear of the left rail, near the top. Clamped on first paint below in
    // case the window is small.
    x: Math.min(220, Math.max(8, window.innerWidth - 400)),
    y: 90
  }));
  const [size, setSize] = useState({ w: 380, h: 520 });

  const [query, setQuery] = useState("");
  const [asked, setAsked] = useState(null);      // the question that produced `results`
  const [results, setResults] = useState(null);  // null = nothing asked yet

  const drag = useRef(null);
  const resize = useRef(null);

  const ctx = CONTEXT[currentView] || FALLBACK_CONTEXT;

  /* ---- drag and resize, on window listeners so a fast drag does not
          "fall off" the header ------------------------------------------ */
  useEffect(() => {
    const onMove = (e) => {
      if (drag.current) {
        const nx = e.clientX - drag.current.dx;
        const ny = e.clientY - drag.current.dy;
        // Keep at least a sliver on screen — a panel dragged fully off is
        // unrecoverable without knowing to close and reopen it.
        setPos({
          x: Math.max(-size.w + 80, Math.min(window.innerWidth - 60, nx)),
          y: Math.max(0, Math.min(window.innerHeight - 40, ny))
        });
      } else if (resize.current) {
        setSize({
          w: Math.max(300, Math.min(760, resize.current.w + (e.clientX - resize.current.x))),
          h: Math.max(280, Math.min(window.innerHeight - 40, resize.current.h + (e.clientY - resize.current.y)))
        });
      }
    };
    const onUp = () => { drag.current = null; resize.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [size.w, size.h]);

  const startDrag = (e) => {
    if (e.button !== 0) return;
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.preventDefault();
  };
  const startResize = (e) => {
    if (e.button !== 0) return;
    resize.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    e.preventDefault();
    e.stopPropagation();
  };

  /* ---- asking ---------------------------------------------------------- */
  const ask = (q) => {
    const text = String(q ?? query).trim();
    if (!text) return;
    setAsked(text);
    setResults(search(text));
  };

  const openTopic = (id) => {
    const e = HOW_TO.find(h => h.id === id);
    if (!e) return;
    setAsked(e.title);
    setResults([e]);
  };

  // Topics offered when nothing matches — the honest version of "I don't know".
  const suggestions = useMemo(() => {
    const rel = (ctx.related || []).map(id => HOW_TO.find(h => h.id === id)).filter(Boolean);
    if (rel.length >= 3) return rel.slice(0, 5);
    const seen = new Set(rel.map(r => r.id));
    return [...rel, ...HOW_TO.filter(h => !seen.has(h.id)).slice(0, 5 - rel.length)];
  }, [ctx]);

  const Answer = ({ entry }) => (
    <div style={P.card}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#111827", marginBottom: 7 }}>
        {entry.title}
      </div>
      <div style={P.where}>{entry.where}</div>
      {entry.steps.map((s, i) => (
        <div key={i} style={P.step}>
          <span style={{ color: "#0f766e", fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
          <span>{s}</span>
        </div>
      ))}
      {entry.note && <div style={P.note}>{entry.note}</div>}
      {entry.view && goTo && (
        <button style={P.go} onClick={() => goTo(entry.view)}>
          Take me there →
        </button>
      )}
    </div>
  );

  return (
    <div style={P.panel(pos.x, pos.y, size.w, size.h)} role="dialog" aria-label="Navigator">
      {/* ---- header: drag handle + close ---- */}
      <div style={P.head} onMouseDown={startDrag}>
        <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.2 }}>Navigator</span>
        <button
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}   // closing must not start a drag
          aria-label="Close navigator"
          title="Close"
          style={{ border: "none", background: "rgba(255,255,255,0.15)", color: "#fff",
                   width: 22, height: 22, borderRadius: 4, cursor: "pointer",
                   fontSize: 15, lineHeight: 1, fontFamily: "inherit" }}>
          ×
        </button>
      </div>

      {/* ---- body ---- */}
      <div style={P.body}>
        {/* WHERE YOU ARE — always shown, always current */}
        <div style={P.label}>Where you are</div>
        <div style={{ ...P.card, background: "#f8fafc", borderColor: "#cbd5e1" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f766e" }}>{ctx.title}</div>
          <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginTop: 4 }}>
            {ctx.blurb}
          </div>
          {!!(ctx.can || []).length && (
            <>
              <div style={{ ...P.label, marginTop: 9, marginBottom: 4 }}>From here you can</div>
              {ctx.can.map((c, i) => (
                <div key={i} style={P.step}>
                  <span style={{ color: "#0f766e", flexShrink: 0 }}>•</span><span>{c}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* ANSWER */}
        {results !== null && (
          <>
            <div style={P.label}>
              {results.length ? "Answer" : "No answer for that"}
            </div>
            {asked && (
              <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", marginBottom: 8 }}>
                “{asked}”
              </div>
            )}

            {results.length > 0 && results.map(e => <Answer key={e.id} entry={e} />)}

            {results.length === 0 && (
              <div style={P.card}>
                <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55 }}>
                  I don't have a guide for that one. The Navigator only answers from a written
                  list of tasks, so it never guesses a menu path that doesn't exist — which does
                  mean it has gaps.
                </div>
                <div style={{ ...P.label, marginTop: 10, marginBottom: 3 }}>
                  Try one of these
                </div>
                {suggestions.map(s => (
                  <button key={s.id} style={P.link} onClick={() => openTopic(s.id)}>
                    → {s.title}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Before anything is asked, offer what is relevant to this screen. */}
        {results === null && (
          <>
            <div style={P.label}>Common tasks from here</div>
            <div style={P.card}>
              {suggestions.map(s => (
                <button key={s.id} style={P.link} onClick={() => openTopic(s.id)}>
                  → {s.title}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ---- ask ---- */}
      <div style={P.foot}>
        <div style={{ ...P.label, marginBottom: 4 }}>Ask a question</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            style={P.input}
            value={query}
            placeholder="e.g. How do I create a staff schedule?"
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") ask(); }}
          />
          <button
            onClick={() => ask()}
            style={{ border: "none", background: "#0f766e", color: "#fff", borderRadius: 5,
                     padding: "0 14px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                     fontFamily: "inherit", flexShrink: 0 }}>
            Ask
          </button>
        </div>
        {results !== null && (
          <button
            style={{ ...P.link, marginTop: 6, fontSize: 11.5 }}
            onClick={() => { setResults(null); setAsked(null); setQuery(""); }}>
            ← Clear
          </button>
        )}
      </div>

      <div style={P.grip} onMouseDown={startResize} title="Resize" />
    </div>
  );
}

// Exported so a future maintainer can check coverage, or a test can assert that
// every `view` in HOW_TO is a route App.js actually renders.
export { HOW_TO, CONTEXT };
